import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Sparkles, Shield, Zap } from 'lucide-react';
import ImageUpload from './components/ImageUpload';
import AnalysisResults from './components/AnalysisResults';
import Header from './components/Header';
import Footer from './components/Footer';
import { initPostHog, trackPageView, getSessionId } from './utils/analytics';

const AppContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
`;

const MainContent = styled.main`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 2rem;
  max-width: 1400px;
  margin: 0 auto;
  width: 100%;
`;

const HeroSection = styled.section`
  text-align: center;
  margin-bottom: 5rem;
  color: #1a1a1a;
  padding: 5rem 0 3rem;
  animation: fadeInUp 0.8s ease-out;
  
  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(30px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const HeroLabel = styled.div`
  font-size: 0.875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.15em;
  color: #3a5cf7;
  margin-bottom: 1.25rem;
  display: inline-block;
  padding: 0.5rem 1.25rem;
  background: linear-gradient(135deg, rgba(58, 92, 247, 0.1) 0%, rgba(58, 92, 247, 0.05) 100%);
  border-radius: 50px;
  border: 1px solid rgba(58, 92, 247, 0.2);
`;

const HeroTitle = styled.h1`
  font-size: 4rem;
  font-weight: 800;
  margin-bottom: 1.75rem;
  color: #0f172a;
  line-height: 1.1;
  letter-spacing: -0.02em;
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  
  @media (max-width: 768px) {
    font-size: 2.75rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.375rem;
  font-weight: 400;
  margin-bottom: 3rem;
  color: #64748b;
  max-width: 750px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.7;
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  margin-bottom: 3rem;
  width: 100%;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FeatureCard = styled.div`
  background: white;
  border-radius: 20px;
  padding: 2.5rem;
  text-align: center;
  color: #1a1a1a;
  border: 1px solid #e5e7eb;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  aspect-ratio: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #3a5cf7, #8b5cf6);
    transform: scaleX(0);
    transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  }
  
  &:hover {
    transform: translateY(-8px) scale(1.02);
    box-shadow: 0 25px 50px -12px rgba(58, 92, 247, 0.25);
    border-color: rgba(58, 92, 247, 0.3);
    
    &::before {
      transform: scaleX(1);
    }
  }
`;

const FeatureIcon = styled.div`
  width: 72px;
  height: 72px;
  background: linear-gradient(135deg, #eff6ff 0%, #e0e7ff 100%);
  border-radius: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1.5rem;
  color: #3a5cf7;
  border: 2px solid rgba(58, 92, 247, 0.1);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 6px -1px rgba(58, 92, 247, 0.1);
  
  ${FeatureCard}:hover & {
    transform: scale(1.1) rotate(5deg);
    background: linear-gradient(135deg, #dbeafe 0%, #e0e7ff 100%);
    box-shadow: 0 8px 12px -2px rgba(58, 92, 247, 0.2);
  }
`;

const FeatureTitle = styled.h3`
  font-size: 1.375rem;
  font-weight: 700;
  margin-bottom: 0.75rem;
  color: #0f172a;
  letter-spacing: -0.01em;
`;

const FeatureDescription = styled.p`
  font-size: 0.95rem;
  color: #64748b;
  line-height: 1.6;
  font-weight: 400;
`;

const UploadSection = styled.section`
  background: white;
  border-radius: 28px;
  padding: 4rem;
  width: 100%;
  max-width: 100%;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  border: 1px solid rgba(226, 232, 240, 0.8);
  position: relative;
  overflow: hidden;
  animation: fadeIn 0.6s ease-out;
  
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #3a5cf7, #8b5cf6, #3a5cf7);
    background-size: 200% 100%;
    animation: shimmer 3s ease-in-out infinite;
  }
  
  @keyframes shimmer {
    0%, 100% {
      background-position: 0% 50%;
    }
    50% {
      background-position: 100% 50%;
    }
  }
  
  @media (max-width: 768px) {
    padding: 2.5rem 1.5rem;
  }
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 800;
  color: #0f172a;
  text-align: center;
  margin-bottom: 1rem;
  letter-spacing: -0.02em;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const SectionSubtitle = styled.p`
  color: #64748b;
  text-align: center;
  margin-bottom: 3rem;
  font-size: 1.125rem;
  line-height: 1.6;
  font-weight: 400;
`;

const DisclaimerSection = styled.div`
  width: 100%;
  max-width: 100%;
  margin: 3rem auto 0;
  padding: 1.5rem;
  background: #f9fafb;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  text-align: center;
`;

const DisclaimerText = styled.p`
  color: #6b7280;
  font-size: 0.875rem;
  line-height: 1.6;
  margin: 0;
`;

function App() {
  const [analysisData, setAnalysisData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    // Initialize analytics (PostHog loaded via snippet, just get reference)
    initPostHog();
    // Wait a bit for PostHog to fully load
    setTimeout(() => {
      trackPageView('home');
    }, 100);
  }, []);

  const handleAnalysisComplete = (data) => {
    setAnalysisData(data);
    // Track successful analysis
    const { trackPhotoUpload } = require('./utils/analytics');
    trackPhotoUpload({
      has_results: true,
      has_after_image: !!data.after_url,
      has_heatmap: !!data.visuals?.heatmap
    });
  };

  const handleReset = () => {
    setAnalysisData(null);
    trackPageView('home');
  };

  return (
    <AppContainer>
      <Header />
      <MainContent>
        <HeroSection>
          <HeroLabel>Revolutionizing Plastic Surgery</HeroLabel>
          <HeroTitle>See Your Perfect Nose Before Surgery</HeroTitle>
          <HeroSubtitle>
            Discover your best self with AI-powered cosmetic analysis.
          </HeroSubtitle>
          
          <FeaturesGrid>
            <FeatureCard>
              <FeatureIcon>
                <Sparkles size={24} />
              </FeatureIcon>
              <FeatureTitle>AI-Powered Analysis</FeatureTitle>
              <FeatureDescription>
                Advanced facial analysis using computer vision to identify enhancement opportunities
              </FeatureDescription>
            </FeatureCard>
            
            <FeatureCard>
              <FeatureIcon>
                <Shield size={24} />
              </FeatureIcon>
              <FeatureTitle>Medical-Grade Precision</FeatureTitle>
              <FeatureDescription>
                Based on established beauty standards and facial harmony principles
              </FeatureDescription>
            </FeatureCard>
            
            <FeatureCard>
              <FeatureIcon>
                <Zap size={24} />
              </FeatureIcon>
              <FeatureTitle>Instant Results</FeatureTitle>
              <FeatureDescription>
                Get your personalized analysis and before/after preview in seconds
              </FeatureDescription>
            </FeatureCard>
          </FeaturesGrid>
        </HeroSection>

        <UploadSection id="upload-section">
          <SectionTitle>Upload Your Photo</SectionTitle>
          <SectionSubtitle>
            Take a selfie or upload a clear front-facing photo to get started
          </SectionSubtitle>
          
          {!analysisData ? (
            <ImageUpload 
              onAnalysisComplete={handleAnalysisComplete}
              isLoading={isLoading}
              setIsLoading={setIsLoading}
            />
          ) : (
            <AnalysisResults 
              data={analysisData}
              onReset={handleReset}
            />
          )}
        </UploadSection>

        <DisclaimerSection>
          <DisclaimerText>
            This is a demonstration tool for educational purposes. Results are simulated and should not be considered as medical advice. Consult with qualified professionals for actual cosmetic procedures.
          </DisclaimerText>
        </DisclaimerSection>
      </MainContent>
      <Footer />
    </AppContainer>
  );
}

export default App;
